import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Users, ShoppingCart, DollarSign, Percent, Package, Store as StoreIcon, Shield } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AdminDashboard() {
    const { t, lang } = useLang();
    const [ov, setOv] = useState(null);
    const [financialSummary, setFinancialSummary] = useState(null);
    const [financialSummaryError, setFinancialSummaryError] = useState(false);
    const [paymentSummary, setPaymentSummary] = useState(null);
    const [paymentSummaryError, setPaymentSummaryError] = useState(false);
    const [commission, setCommission] = useState(10);
    const [shippingFee, setShippingFee] = useState(20);

    useEffect(() => {
        api.get("/admin/overview").then((r) => setOv(r.data));

        api.get("/admin/payouts/summary")
            .then((r) => {
                setFinancialSummary(r.data);
                setFinancialSummaryError(false);
            })
            .catch(() => {
                setFinancialSummary(null);
                setFinancialSummaryError(true);
            });

        api.get("/admin/payments/summary")
            .then((r) => {
                setPaymentSummary(r.data);
                setPaymentSummaryError(false);
            })
            .catch(() => {
                setPaymentSummary(null);
                setPaymentSummaryError(true);
            });

        api.get("/admin/settings").then((r) => {
            setCommission(r.data.percentage);
            setShippingFee(
                r.data.shipping_fee_per_store ?? 20
            );
        });
    }, []);

    const saveCommission = async () => {
        try {
            await api.put("/admin/commission", {
                percentage: parseFloat(commission),
            });

            toast.success(
                lang === "ar"
                    ? "تم حفظ نسبة العمولة"
                    : "Commission saved"
            );
        } catch {
            toast.error("Error");
        }
    };

    const saveShipping = async () => {
        try {
            const fee = Number(shippingFee);

            if (!Number.isFinite(fee) || fee < 0) {
                toast.error(
                    lang === "ar"
                        ? "أدخل سعر شحن صحيح"
                        : "Enter a valid shipping fee"
                );
                return;
            }

            await api.put("/admin/shipping", {
                fee_per_store: fee,
            });

            toast.success(
                lang === "ar"
                    ? "تم حفظ سعر الشحن"
                    : "Shipping fee saved"
            );

        } catch {
            toast.error(
                lang === "ar"
                    ? "تعذر حفظ سعر الشحن"
                    : "Could not save shipping fee"
            );
        }
    };

    const money = (value) =>
        `${Number(value ?? 0).toFixed(2)} ${t("SAR")}`;

    if (!ov) return <Layout><div className="min-h-[50vh] grid place-items-center text-muted-foreground">...</div></Layout>;

    return (
        <Layout>
            <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
                <h1 className="text-3xl md:text-4xl font-black text-espresso">{t("adminDashboard")}</h1>

                <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">

                    <StatCard
                        label={t("users")}
                        value={ov.counts.users}
                        icon={
                            <Users className="w-5 h-5" />
                        }
                    />

                    <StatCard
                        label={t("totalOrders")}
                        value={
                            ov.counts.total_orders
                            ?? (
                                ov.counts.orders_paid
                                + ov.counts.orders_pending
                            )
                        }
                        icon={
                            <ShoppingCart className="w-5 h-5" />
                        }
                    />

                    <StatCard
                        label={
                            lang === "ar"
                                ? "\u0627\u0644\u0637\u0644\u0628\u0627\u062a \u0627\u0644\u0645\u062f\u0641\u0648\u0639\u0629"
                                : "Paid orders"
                        }
                        value={ov.counts.orders_paid ?? 0}
                        icon={
                            <DollarSign className="w-5 h-5" />
                        }
                    />

                    <StatCard
                        label={
                            lang === "ar"
                                ? "\u062a\u0645 \u0627\u0644\u062a\u0648\u0635\u064a\u0644"
                                : "Delivered orders"
                        }
                        value={
                            ov.counts.orders_delivered
                            ?? 0
                        }
                        icon={
                            <Package className="w-5 h-5" />
                        }
                    />
                    <StatCard
                        label={t("products")}
                        value={ov.counts.products}
                        icon={
                            <Package className="w-5 h-5" />
                        }
                    />

                    <StatCard
                        label={t("stores")}
                        value={ov.counts.stores}
                        icon={
                            <StoreIcon className="w-5 h-5" />
                        }
                    />

                </div>

                <div className="mt-10">
                    <div className="mb-4">
                        <h2 className="text-xl font-black text-espresso">
                            {lang === "ar"
                                ? "\u0627\u0644\u0645\u062f\u0641\u0648\u0639\u0627\u062a \u0627\u0644\u0625\u0644\u0643\u062a\u0631\u0648\u0646\u064a\u0629"
                                : "Electronic payments"}
                        </h2>

                        <p className="text-sm text-muted-foreground mt-1">
                            {lang === "ar"
                                ? "\u0645\u0644\u062e\u0635 \u0645\u062f\u0641\u0648\u0639\u0627\u062a \u0645\u064a\u0633\u0631 \u0627\u0644\u0645\u062d\u0635\u0644\u0629 \u0645\u0646 \u0627\u0644\u0639\u0645\u0644\u0627\u0621 \u0648\u0627\u0644\u0627\u0633\u062a\u0631\u062c\u0627\u0639\u0627\u062a \u0627\u0644\u0645\u0631\u062a\u0628\u0637\u0629 \u0628\u0647\u0627."
                                : "Moyasar payments collected from customers and related refunds."}
                        </p>
                    </div>

                    {paymentSummary ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            <StatCard
                                label={
                                    lang === "ar"
                                        ? "\u0625\u062c\u0645\u0627\u0644\u064a \u0627\u0644\u0645\u062d\u0635\u0644 \u0642\u0628\u0644 \u0627\u0644\u0627\u0633\u062a\u0631\u062c\u0627\u0639\u0627\u062a"
                                        : "Gross collected before refunds"
                                }
                                value={money(
                                    paymentSummary.gross_collected
                                )}
                                icon={
                                    <DollarSign className="w-5 h-5" />
                                }
                            />

                            <StatCard
                                label={
                                    lang === "ar"
                                        ? "\u0627\u0644\u0645\u0628\u0627\u0644\u063a \u0627\u0644\u0645\u0633\u062a\u0631\u062c\u0639\u0629"
                                        : "Refunded"
                                }
                                value={money(
                                    paymentSummary.refunded
                                )}
                                icon={
                                    <Shield className="w-5 h-5" />
                                }
                                warning={
                                    Number(
                                        paymentSummary.refunded
                                        ?? 0
                                    ) > 0
                                }
                            />

                            <StatCard
                                label={
                                    lang === "ar"
                                        ? "\u0635\u0627\u0641\u064a \u0627\u0644\u0645\u062f\u0641\u0648\u0639\u0627\u062a \u0628\u0639\u062f \u0627\u0644\u0627\u0633\u062a\u0631\u062c\u0627\u0639\u0627\u062a"
                                        : "Net collected after refunds"
                                }
                                value={money(
                                    paymentSummary.net_collected
                                )}
                                icon={
                                    <DollarSign className="w-5 h-5" />
                                }
                            />

                            <StatCard
                                label={
                                    lang === "ar"
                                        ? "\u0631\u0633\u0648\u0645 \u0627\u0644\u0634\u062d\u0646 \u0627\u0644\u0645\u062d\u062a\u0641\u0638 \u0628\u0647\u0627"
                                        : "Shipping retained"
                                }
                                value={money(
                                    paymentSummary.shipping_retained
                                )}
                                icon={
                                    <Package className="w-5 h-5" />
                                }
                            />
                        </div>
                    ) : (
                        <div className="bg-white border border-border rounded-2xl p-5 text-sm text-muted-foreground">
                            {paymentSummaryError
                                ? (
                                    lang === "ar"
                                        ? "\u062a\u0639\u0630\u0631 \u062a\u062d\u0645\u064a\u0644 \u0645\u0644\u062e\u0635 \u0627\u0644\u0645\u062f\u0641\u0648\u0639\u0627\u062a."
                                        : "Could not load payment summary."
                                )
                                : (
                                    lang === "ar"
                                        ? "\u062c\u0627\u0631\u064a \u062a\u062d\u0645\u064a\u0644 \u0645\u0644\u062e\u0635 \u0627\u0644\u0645\u062f\u0641\u0648\u0639\u0627\u062a..."
                                        : "Loading payment summary..."
                                )}
                        </div>
                    )}
                </div>


                <div className="mt-10">
                    <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
                        <div>
                            <h2 className="text-xl font-black text-espresso">
                                {lang === "ar"
                                    ? "\u0627\u0644\u0645\u0644\u062e\u0635 \u0627\u0644\u0645\u0627\u0644\u064a"
                                    : "Financial summary"}
                            </h2>

                            <p className="text-sm text-muted-foreground mt-1">
                                {lang === "ar"
                                    ? "\u0627\u0644\u0623\u0631\u0642\u0627\u0645 \u0645\u0628\u0646\u064a\u0629 \u0639\u0644\u0649 \u0645\u0633\u062a\u062d\u0642\u0627\u062a \u0627\u0644\u0628\u0627\u0626\u0639\u064a\u0646 \u0627\u0644\u0641\u0639\u0644\u064a\u0629 \u0648\u062d\u0627\u0644\u0627\u062a \u0627\u0644\u0627\u0633\u062a\u0631\u062c\u0627\u0639."
                                    : "Based on actual seller payouts and refund status."}
                            </p>
                        </div>

                        {financialSummary && (
                            <Link
                                to="/admin/payouts"
                                className="text-sm text-terracotta font-bold hover:underline"
                            >
                                {lang === "ar"
                                    ? "\u0639\u0631\u0636 \u0645\u0633\u062a\u062d\u0642\u0627\u062a \u0627\u0644\u0628\u0627\u0626\u0639\u064a\u0646"
                                    : "View seller payouts"}
                            </Link>
                        )}
                    </div>

                    {financialSummary ? (
                        <>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                <StatCard
                                    label={
                                        lang === "ar"
                                            ? "\u0627\u0644\u0645\u0628\u064a\u0639\u0627\u062a \u0627\u0644\u0645\u0643\u062a\u0645\u0644\u0629"
                                            : "Completed product sales"
                                    }
                                    value={money(
                                        financialSummary.completed_product_sales
                                    )}
                                    icon={
                                        <DollarSign className="w-5 h-5" />
                                    }
                                />

                                <StatCard
                                    label={
                                        lang === "ar"
                                            ? "\u0639\u0645\u0648\u0644\u0629 \u0627\u0644\u0645\u0646\u0635\u0629 \u0627\u0644\u0645\u0643\u062a\u0633\u0628\u0629"
                                            : "Platform commission earned"
                                    }
                                    value={money(
                                        financialSummary.platform_commission_earned
                                    )}
                                    icon={
                                        <Percent className="w-5 h-5" />
                                    }
                                />

                                <StatCard
                                    label={
                                        lang === "ar"
                                            ? "\u0645\u0633\u062a\u062d\u0642\u0627\u062a \u0627\u0644\u0628\u0627\u0626\u0639\u064a\u0646 \u0627\u0644\u0645\u0639\u0644\u0642\u0629"
                                            : "Seller payouts pending"
                                    }
                                    value={money(
                                        financialSummary.seller_net_pending
                                    )}
                                    icon={
                                        <ShoppingCart className="w-5 h-5" />
                                    }
                                />

                                <StatCard
                                    label={
                                        lang === "ar"
                                            ? "\u062c\u0627\u0647\u0632 \u0644\u0644\u062a\u062d\u0648\u064a\u0644"
                                            : "Ready for transfer"
                                    }
                                    value={money(
                                        financialSummary.seller_net_available
                                    )}
                                    icon={
                                        <DollarSign className="w-5 h-5" />
                                    }
                                />

                                <StatCard
                                    label={
                                        lang === "ar"
                                            ? "\u062a\u0645 \u062a\u062d\u0648\u064a\u0644\u0647 \u0644\u0644\u0628\u0627\u0626\u0639\u064a\u0646"
                                            : "Transferred to sellers"
                                    }
                                    value={money(
                                        financialSummary.seller_net_paid
                                    )}
                                    icon={
                                        <DollarSign className="w-5 h-5" />
                                    }
                                />

                                <StatCard
                                    label={
                                        lang === "ar"
                                            ? `\u0645\u062d\u0648\u0644 \u062a\u062d\u062a \u0627\u0644\u0645\u0631\u0627\u062c\u0639\u0629 (${financialSummary.paid_under_review_count ?? 0})`
                                            : `Paid under review (${financialSummary.paid_under_review_count ?? 0})`
                                    }
                                    value={money(
                                        financialSummary.seller_net_paid_under_review
                                    )}
                                    icon={
                                        <Shield className="w-5 h-5" />
                                    }
                                    warning={
                                        Number(
                                            financialSummary.seller_net_paid_under_review
                                            ?? 0
                                        ) > 0
                                    }
                                />
                            </div>

                            <div className="mt-3 text-sm text-muted-foreground">
                                {lang === "ar"
                                    ? "\u0625\u062c\u0645\u0627\u0644\u064a \u0645\u0633\u062a\u062d\u0642\u0627\u062a \u0627\u0644\u0628\u0627\u0626\u0639\u064a\u0646 \u063a\u064a\u0631 \u0627\u0644\u0645\u062d\u0648\u0644\u0629:"
                                    : "Total seller payouts not yet transferred:"}
                                {" "}
                                <span className="font-black text-espresso">
                                    {money(
                                        financialSummary.seller_net_outstanding
                                    )}
                                </span>
                            </div>
                        </>
                    ) : (
                        <div className="bg-white border border-border rounded-2xl p-5 text-sm text-muted-foreground">
                            {financialSummaryError
                                ? (
                                    lang === "ar"
                                        ? "\u062a\u0639\u0630\u0631 \u062a\u062d\u0645\u064a\u0644 \u0627\u0644\u0645\u0644\u062e\u0635 \u0627\u0644\u0645\u0627\u0644\u064a."
                                        : "Could not load financial summary."
                                )
                                : (
                                    lang === "ar"
                                        ? "\u062c\u0627\u0631\u064a \u062a\u062d\u0645\u064a\u0644 \u0627\u0644\u0645\u0644\u062e\u0635 \u0627\u0644\u0645\u0627\u0644\u064a..."
                                        : "Loading financial summary..."
                                )}
                        </div>
                    )}
                </div>

                <div className="mt-10 grid lg:grid-cols-3 gap-6">
                    <div className="bg-white border border-border rounded-2xl p-6 lg:col-span-2">

                        <h2 className="font-black text-espresso mb-2">
                            {lang === "ar"
                                ? "الإدارة السريعة"
                                : "Quick management"}
                        </h2>

                        <p className="text-sm text-muted-foreground mb-5">
                            {lang === "ar"
                                ? "الوصول السريع للمهام اليومية المهمة"
                                : "Quick access to important daily operations"}
                        </p>

                        <div className="grid sm:grid-cols-3 gap-4">

                            <Link
                                to="/admin/orders"
                                className="border border-border rounded-xl p-5 hover:bg-surface transition-colors"
                            >
                                <ShoppingCart className="w-6 h-6 text-terracotta mb-3" />

                                <div className="font-black text-espresso">
                                    {lang === "ar"
                                        ? "إدارة الطلبات"
                                        : "Manage orders"}
                                </div>

                                <div className="text-xs text-muted-foreground mt-1">
                                    {lang === "ar"
                                        ? "البحث، الفلترة والاسترجاع"
                                        : "Search, filter and refunds"}
                                </div>
                            </Link>


                            <Link
                                to="/admin/payouts"
                                className="border border-border rounded-xl p-5 hover:bg-surface transition-colors"
                            >
                                <DollarSign className="w-6 h-6 text-terracotta mb-3" />

                                <div className="font-black text-espresso">
                                    {lang === "ar"
                                        ? "مستحقات البائعين"
                                        : "Seller payouts"}
                                </div>

                                <div className="text-xs text-muted-foreground mt-1">
                                    {lang === "ar"
                                        ? "مراجعة المبالغ والتحويلات"
                                        : "Review balances and transfers"}
                                </div>
                            </Link>


                            <Link
                                to="/admin/verifications"
                                className="border border-border rounded-xl p-5 hover:bg-surface transition-colors"
                            >
                                <Shield className="w-6 h-6 text-terracotta mb-3" />

                                <div className="font-black text-espresso">
                                    {lang === "ar"
                                        ? "الموافقات"
                                        : "Approvals"}
                                </div>

                                <div className="text-xs text-muted-foreground mt-1">
                                    {lang === "ar"
                                        ? "مراجعة طلبات البائعين"
                                        : "Review seller requests"}
                                </div>
                            </Link>

                        </div>

                    </div>

                    <div className="bg-white border border-border rounded-2xl p-6" data-testid="commission-panel">
                        <h2 className="font-black text-espresso mb-4">{t("commissionSettings")}</h2>
                        <Label>{t("commissionPct")}</Label>
                        <Input type="number" step="0.5" min="0" max="100" value={commission} onChange={(e) => setCommission(e.target.value)} className="mt-1.5" data-testid="commission-input" />
                        <Button onClick={saveCommission} className="mt-4 w-full rounded-full bg-terracotta text-white" data-testid="save-commission-btn">
                            {t("save")}
                        </Button>
                        <div className="mt-6 pt-5 border-t border-border">
                            <h3 className="font-black text-espresso mb-3">
                                {lang === "ar"
                                    ? "إعدادات الشحن"
                                    : "Shipping settings"}
                            </h3>

                            <Label>
                                {lang === "ar"
                                    ? "سعر الشحنة لكل متجر"
                                    : "Shipping fee per store"}
                                {" "}({t("SAR")})
                            </Label>

                            <Input
                                type="number"
                                min="0"
                                max="500"
                                step="0.5"
                                value={shippingFee}
                                onChange={(e) =>
                                    setShippingFee(e.target.value)
                                }
                                className="mt-1.5"
                                data-testid="shipping-fee-input"
                            />

                            <div className="text-xs text-muted-foreground mt-2">
                                {lang === "ar"
                                    ? "يتم احتساب شحنة مستقلة لكل متجر موجود في الطلب."
                                    : "One shipment is charged for each store in the order."}
                            </div>

                            <Button
                                onClick={saveShipping}
                                className="mt-4 w-full rounded-full bg-terracotta text-white"
                                data-testid="save-shipping-btn"
                            >
                                {t("save")}
                            </Button>
                        </div>

                        <div className="mt-4 pt-4 border-t border-border space-y-3">

                            <Link
                                to="/admin/orders"
                                className="text-sm text-terracotta font-bold hover:underline flex items-center gap-1"
                                data-testid="link-admin-orders"
                            >
                                <ShoppingCart className="w-4 h-4" />
                                {lang === "ar"
                                    ? "\u0625\u062f\u0627\u0631\u0629 \u0627\u0644\u0637\u0644\u0628\u0627\u062a \u0648\u0627\u0644\u0627\u0633\u062a\u0631\u062c\u0627\u0639"
                                    : "Orders & refunds"}
                            </Link>

                            <Link
                                to="/admin/payouts"
                                className="text-sm text-terracotta font-bold hover:underline flex items-center gap-1"
                                data-testid="link-payouts"
                            >
                                <DollarSign className="w-4 h-4" />
                                {lang === "ar"
                                    ? "\u0645\u0633\u062a\u062d\u0642\u0627\u062a \u0627\u0644\u0628\u0627\u0626\u0639\u064a\u0646"
                                    : "Seller payouts"}
                            </Link>

                            <Link to="/admin/verifications" className="text-sm text-terracotta font-bold hover:underline flex items-center gap-1" data-testid="link-verifications">
                                <Shield className="w-4 h-4" />
                                {t("pendingApprovals")}
                            </Link>
                        </div>
                    </div>

                    <div className="bg-white border border-border rounded-2xl p-6 lg:col-span-3">
                        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">

                            <h2 className="font-black text-espresso">
                                {lang === "ar"
                                    ? "أحدث الطلبات"
                                    : "Recent orders"}
                            </h2>

                            <Link
                                to="/admin/orders"
                                className="text-sm text-terracotta font-bold hover:underline"
                            >
                                {lang === "ar"
                                    ? "عرض وإدارة جميع الطلبات"
                                    : "View and manage all orders"}
                            </Link>

                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="text-muted-foreground text-xs">
                                    <tr className="border-b border-border">
                                        <th className="text-start py-2">#</th>
                                        <th className="text-start py-2">{t("total")}</th>
                                        <th className="text-start py-2">{t("status")}</th>
                                        <th className="text-start py-2">{lang === "ar" ? "التاريخ" : "Date"}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ov.recent_orders.map((o) => (
                                        <tr key={o.order_id} className="border-b border-border/50">
                                            <td className="py-2 font-mono text-xs">{o.order_id.slice(-6)}</td>
                                            <td className="py-2 font-bold text-terracotta">{o.total.toFixed(2)}</td>
                                            <td className="py-2">{o.status}</td>
                                            <td className="py-2 text-muted-foreground text-xs">{new Date(o.created_at).toLocaleDateString()}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
}

function StatCard({
    label,
    value,
    icon,
    warning = false,
}) {
    return (
        <div
            className={`border rounded-2xl p-5 ${
                warning
                    ? "bg-amber-50 border-amber-300"
                    : "bg-white border-border"
            }`}
            data-testid={`admin-stat-${label}`}
        >
            <div className="flex items-center justify-between">
                <div className="text-xs text-muted-foreground uppercase tracking-wide">
                    {label}
                </div>

                <div
                    className={
                        warning
                            ? "text-amber-700"
                            : "text-terracotta"
                    }
                >
                    {icon}
                </div>
            </div>

            <div
                className={`text-2xl font-black mt-2 ${
                    warning
                        ? "text-amber-800"
                        : "text-espresso"
                }`}
            >
                {value}
            </div>
        </div>
    );
}
